import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Badge, Button, Alert, Spinner } from 'react-bootstrap';
import { Trophy, ChevronLeft, ChevronRight, ArrowClockwise } from 'react-bootstrap-icons';
import moment from 'moment';
import { shiftLeaderboardAPI } from '../../services';
import { getErrorMessage } from '../../utils/apiClient';

// Mốc mặc định = Thứ 2 của TUẦN SAU (đúng tuần mà bảng xếp hạng đang chạy trên app nhân viên
// hiển thị: "Bảng xếp hạng đóng góp ca tuần sau").
const nextWeekMonday = () => moment().add(1, 'week').startOf('isoWeek').format('YYYY-MM-DD');

const RANK_STYLE = {
  1: { bg: '#fffbeb', border: '#fcd34d', badge: 'warning', medal: '🥇' },
  2: { bg: '#f8fafc', border: '#cbd5e1', badge: 'secondary', medal: '🥈' },
  3: { bg: '#fff7ed', border: '#fdba74', badge: 'warning', medal: '🥉' },
};

const formatWeekLabel = (weekStart, weekEnd) => {
  if (!weekStart || !weekEnd) return '';
  // week_end là mốc loại trừ (07:00 Thứ 2 tuần kế) — lùi 1 ngày để ra đúng Chủ Nhật.
  return `${moment(weekStart).format('DD-MM')} - ${moment(weekEnd).subtract(1, 'day').format('DD-MM')}`;
};

/**
 * Tab "Xếp hạng đóng góp ca" của màn Quản lý ca làm việc (#131031).
 * Hiển thị đúng bảng xếp hạng đang chạy trên app nhân viên, kèm SỐ CA từng người đã đăng ký
 * trong tuần đang xem. Bảng chỉ đọc; nút "Chốt tuần" (chỉ ADMIN, truyền `onCloseWeek`) mở hộp chốt
 * bảng xếp hạng + cộng thưởng top kèm trừ TLT đăng ký ca cho ĐÚNG tuần đang xem (lệnh sếp 23/10/2026:
 * không còn cron tự chốt).
 */
const ShiftLeaderboardTab = ({ isActive, branchId, onCloseWeek }) => {
  const [weekAnchor, setWeekAnchor] = useState(nextWeekMonday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadLeaderboard = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError('');
    try {
      const response = await shiftLeaderboardAPI.getLeaderboard(branchId, weekAnchor);
      setData(response?.data || response || null);
    } catch (err) {
      setData(null);
      setError(`Không tải được bảng xếp hạng đóng góp ca: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, [branchId, weekAnchor]);

  useEffect(() => {
    if (!isActive) return;
    loadLeaderboard();
  }, [isActive, loadLeaderboard]);

  const rewardLine = useMemo(() => {
    const tiers = data?.reward_tiers || [];
    if (tiers.length === 0) return '';
    return `${tiers
      .slice()
      .sort((a, b) => a.rank - b.rank)
      .map((tier) => `Top ${tier.rank}: ${Math.round(tier.ratio * 100)}%`)
      .join(' · ')} TLT`;
  }, [data]);

  const entries = data?.entries || [];
  const isNextWeek = weekAnchor === nextWeekMonday();

  return (
    <Card>
      <Card.Body>
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
          <div className='d-flex align-items-center gap-2'>
            <Trophy
              size={18}
              className='text-warning'
            />
            <h6 className='mb-0 fw-semibold'>Bảng xếp hạng đóng góp ca</h6>
            {data?.is_locked && <Badge bg='dark'>Đã chốt</Badge>}
          </div>

          <div className='d-flex align-items-center gap-1'>
            <Button
              variant='outline-secondary'
              size='sm'
              title='Tuần trước'
              onClick={() =>
                setWeekAnchor((prev) => moment(prev, 'YYYY-MM-DD').subtract(1, 'week').format('YYYY-MM-DD'))
              }
            >
              <ChevronLeft size={14} />
            </Button>
            <span
              className='small fw-semibold px-2'
              data-testid='leaderboard-week-label'
            >
              {data ? formatWeekLabel(data.week_start, data.week_end) : moment(weekAnchor).format('DD-MM')}
            </span>
            <Button
              variant='outline-secondary'
              size='sm'
              title='Tuần sau'
              onClick={() =>
                setWeekAnchor((prev) => moment(prev, 'YYYY-MM-DD').add(1, 'week').format('YYYY-MM-DD'))
              }
            >
              <ChevronRight size={14} />
            </Button>
            {!isNextWeek && (
              <Button
                variant='outline-primary'
                size='sm'
                onClick={() => setWeekAnchor(nextWeekMonday())}
              >
                Tuần sau
              </Button>
            )}
            <Button
              variant='outline-secondary'
              size='sm'
              title='Làm mới'
              onClick={loadLeaderboard}
              disabled={loading}
            >
              <ArrowClockwise size={14} />
            </Button>
            {onCloseWeek && (
              <Button
                variant={data?.is_locked ? 'outline-secondary' : 'danger'}
                size='sm'
                onClick={() => onCloseWeek(moment(weekAnchor, 'YYYY-MM-DD').startOf('isoWeek').format('YYYY-MM-DD'))}
              >
                {data?.is_locked ? 'Xem chốt tuần' : '🔒 Chốt tuần (thưởng + phạt)'}
              </Button>
            )}
          </div>
        </div>

        {rewardLine && (
          <div
            className='mb-3 rounded px-3 py-2 small fw-semibold'
            style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}
          >
            {rewardLine}
          </div>
        )}

        {loading && !data && (
          <div className='text-center text-muted py-4'>
            <Spinner
              animation='border'
              size='sm'
              className='me-2'
            />
            Đang tải bảng xếp hạng...
          </div>
        )}

        {error && !data && <Alert variant='warning'>{error}</Alert>}

        {data && entries.length === 0 && (
          <div className='text-center text-muted py-4'>Chưa có ai đăng ký ca trong tuần này</div>
        )}

        {entries.length > 0 && (
          <div
            className='d-flex flex-column gap-2'
            data-testid='leaderboard-entries'
          >
            {entries.map((entry) => {
              const style = RANK_STYLE[entry.rank];
              return (
                <div
                  key={entry.employee_id}
                  data-testid={`leaderboard-row-${entry.employee_id}`}
                  className='d-flex flex-wrap align-items-center justify-content-between gap-2 rounded px-3 py-2'
                  style={{
                    background: style ? style.bg : '#ffffff',
                    border: `1px solid ${style ? style.border : '#e5e7eb'}`,
                  }}
                >
                  <div className='d-flex align-items-center gap-2 min-w-0'>
                    <span
                      className='d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold'
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: style ? style.border : '#f1f5f9',
                        fontSize: 13,
                      }}
                    >
                      {style ? style.medal : entry.rank}
                    </span>
                    <span className='fw-medium text-truncate'>{entry.employee_name}</span>
                    {entry.reward_ratio !== undefined && entry.reward_ratio !== null && (
                      <Badge bg='warning' text='dark'>
                        +{Math.round(entry.reward_ratio * 100)}%
                      </Badge>
                    )}
                  </div>

                  <div className='d-flex align-items-center gap-2 flex-shrink-0'>
                    <Badge
                      bg='info'
                      data-testid={`leaderboard-shift-count-${entry.employee_id}`}
                    >
                      {entry.shift_count === undefined || entry.shift_count === null
                        ? '— ca'
                        : `${entry.shift_count} ca`}
                    </Badge>
                    <span className='fw-semibold text-secondary'>{Number(entry.points).toFixed(2)} đ</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className='small text-muted mt-3'>
          Số ca = số ca người đó đã đăng ký trong tuần đang xem (gồm cả ca đã được duyệt); điểm đóng góp = mỗi ca
          chia đều cho những người cùng đăng ký ca đó. Tuần đã chốt trước 17-09-2026 chưa lưu sẵn số ca nên được
          tính lại từ đăng ký của tuần đó.
        </div>
      </Card.Body>
    </Card>
  );
};

export default ShiftLeaderboardTab;
